import _WorkDays from './data/work-days.json';
import { ErrorCode, ErrorWCode, errorMessageMap } from '../../exceptions';
import { NetTerms } from '../types';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import dayjs from 'dayjs';
import { CyclicDate } from '../dates/cyclic-date.interface';
import { BillingCycleUnit } from '../../pricing';
import { BillingCycleStruct } from '../../pricing/interfaces/billing-cycle';

dayjs.extend(quarterOfYear);

export type Country = keyof typeof _WorkDays;

/** Runtime check that a string is a country we hold a business-day calendar for (a valid `Country`). */
export const isSupportedCountry = (value: string): value is Country => Object.prototype.hasOwnProperty.call(_WorkDays, value);

const DEFAULT_COUNTRY: Country = (process.env.CUSTOMERS_DEFAULT_COUNTRY as Country) || 'United States';

export const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
type WeekDay = typeof weekDays[number];

const WorkDays = _WorkDays as { [key: string]: WeekDay[] };

export class DateUtils {
  static currentTimeInSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  static calculateBillingEndDate(billingStartDate: Date | undefined, periodInYears: number): Date {
    return dayjs(billingStartDate).add(periodInYears, 'year').subtract(1, 'day').toDate();
  }

  static getDueDate(invoiceDate: Date, netTerms: NetTerms, countryName?: Country | '') {
    if (typeof netTerms === 'number' && netTerms >= 0) {
      return dayjs(invoiceDate).add(netTerms, 'day').toDate();
    }

    const resolvedCountry = countryName || DEFAULT_COUNTRY;
    let businessDays = WorkDays[resolvedCountry];
    if (!businessDays) {
      businessDays = WorkDays[DEFAULT_COUNTRY];
    }

    return this.nextBusinessDay(invoiceDate, businessDays);
  }

  static dayToNumber(day: WeekDay) {
    return weekDays.indexOf(day);
  }

  static nextBusinessDay(date: Date, businessDays: WeekDay[]) {
    const businessDaysNumbers = businessDays.map((dayName: WeekDay) => this.dayToNumber(dayName));

    const holidays: any[] = []; // Add holidays as array of dates

    let nextBusinessDay = dayjs(date);

    do {
      nextBusinessDay = nextBusinessDay.add(1, 'day');
      // If current date is not a business day, add a day and check again
    } while (!this.isBusinessDay(nextBusinessDay, businessDaysNumbers, holidays));

    return nextBusinessDay.toDate();
  }

  static isBusinessDay(date: dayjs.Dayjs, businessDays: number[], holidays: any[]) {
    // Check if the date is a holiday
    for (const holiday of holidays) {
      if (date.isSame(holiday, 'day')) {
        return false;
      }
    }

    return businessDays.includes(date.day());
  }

  static getCyclicDate(date: string | Date, billingCycle: BillingCycleStruct): CyclicDate {
    const dateObj = dayjs(date);
    switch (billingCycle.unit) {
      case BillingCycleUnit.MONTHLY:
        return {
          day: dateObj.get('date'),
          month: dateObj.get('month') + billingCycle.cycle,
        };
      case BillingCycleUnit.YEARLY:
        return {
          day: dateObj.get('date'),
          month: dateObj.get('month') + 1,
          year: dateObj.get('year') + billingCycle.cycle,
        };
      case BillingCycleUnit.ONE_TIME:
        return {
          day: dateObj.get('date'),
          month: dateObj.get('month') + 1,
          year: dateObj.get('year'),
        };
      case BillingCycleUnit.QUARTERLY:
        return {
          day: dateObj.get('date'),
          month: dateObj.get('month') + billingCycle.cycle * 3,
          monthInQuarter: dateObj.get('month') - dateObj.startOf('quarter').get('month') + 1,
        };
      default:
        throw new ErrorWCode(
          errorMessageMap[ErrorCode.BILLING_CYCLE_NOT_SUPPORTED],
          ErrorCode.BILLING_CYCLE_NOT_SUPPORTED,
        );
    }
  }

  static getCyclicPeriodStart(periodCycle: BillingCycleStruct, periodStart: Date, periodEnd: Date): CyclicDate {
    if (periodEnd) {
      switch (periodCycle.unit) {
        case BillingCycleUnit.ONE_TIME: {
          break;
        }
        case BillingCycleUnit.YEARLY:
        case BillingCycleUnit.MONTHLY: {
          periodStart = dayjs(periodEnd).add(1, 'day').toDate();
          break;
        }
        case BillingCycleUnit.QUARTERLY: {
          periodStart = dayjs(periodEnd).startOf('quarter').toDate();
          break;
        }
      }
    }

    return DateUtils.getCyclicDate(periodStart, periodCycle);
  }

  static moveDateForwardToStartOfMonth(baseDate: Date): Date {
    const date = dayjs(baseDate);
    const startOfMonth = date.startOf('month');

    if (date.isSame(startOfMonth, 'day')) {
      return date.toDate();
    } else {
      return date.add(1, 'month').startOf('month').toDate();
    }
  }

  static moveDateBackwardToStartOfMonth(baseDate: Date): Date {
    const date = dayjs(baseDate);
    const startOfMonth = date.startOf('month');

    if (date.isSame(startOfMonth, 'day')) {
      return date.toDate();
    } else {
      return startOfMonth.toDate();
    }
  }
}
